/**
 * Local persistence. No server, no account — everything lives in localStorage.
 *
 * A game in progress is stored with its answers in it. There is no seed to store
 * instead: the answers were drawn at random from a list, and half a game with
 * different words in it on reload would be worse than no resume at all. It does
 * mean a determined player can read tomorrow's word out of devtools, which is a
 * trade every offline word game makes and nobody has ever regretted.
 */

import type { Game } from './game';
import { BOARD_COUNTS, WORD_LENGTHS, type BoardCount, type WordLength } from './types';

const KEY = 'wordle.v1';

/** How the game looks, kept across games rather than per game. */
export interface Settings {
  /**
   * Orange and blue instead of green and yellow.
   *
   * Green/yellow is the pairing most red-green colour blindness cannot tell
   * apart, and this game says everything it has to say in colour.
   */
  highContrast: boolean;
}

/** One config's record. Keyed by `statsKey`, because 4×6 is not 1×5. */
export interface Stat {
  played: number;
  won: number;
  /** Consecutive wins, ending at the most recent game. */
  streak: number;
  best: number;
}

export interface Stored {
  boards: BoardCount;
  length: WordLength;
  settings: Settings;
  /** The game in progress, finished or not — a finished one still wants showing. */
  game: Game | null;
  stats: Record<string, Stat>;
}

export const DEFAULT_BOARDS: BoardCount = 1;
export const DEFAULT_LENGTH: WordLength = 5;
export const DEFAULT_SETTINGS: Settings = { highContrast: false };

export function statsKey(boards: BoardCount, length: WordLength): string {
  return `${boards}x${length}`;
}

export const EMPTY_STAT: Stat = { played: 0, won: 0, streak: 0, best: 0 };

/**
 * Folds a finished game into that config's record.
 *
 * Pure, and takes the outcome rather than the game, so the caller decides when a
 * game counts as over and this cannot double-count a re-render.
 */
export function record(stat: Stat | undefined, won: boolean): Stat {
  const base = stat ?? EMPTY_STAT;
  const streak = won ? base.streak + 1 : 0;
  return {
    played: base.played + 1,
    won: base.won + (won ? 1 : 0),
    streak,
    best: Math.max(base.best, streak),
  };
}

function isGame(value: unknown): value is Game {
  if (typeof value !== 'object' || value === null) return false;
  const game = value as Partial<Game>;
  if (!BOARD_COUNTS.includes(game.boards as BoardCount)) return false;
  if (!WORD_LENGTHS.includes(game.length as WordLength)) return false;
  if (!Array.isArray(game.answers) || game.answers.length !== game.boards) return false;
  if (!Array.isArray(game.guesses)) return false;
  const ofLength = (word: unknown): boolean =>
    typeof word === 'string' && word.length === game.length && /^[a-z]+$/.test(word);
  return game.answers.every(ofLength) && game.guesses.every(ofLength);
}

function isStat(value: unknown): value is Stat {
  if (typeof value !== 'object' || value === null) return false;
  const stat = value as Partial<Stat>;
  return (['played', 'won', 'streak', 'best'] as const).every((field) => {
    const number = stat[field];
    return typeof number === 'number' && Number.isFinite(number) && number >= 0;
  });
}

/**
 * Reads saved state, discarding anything that does not look right.
 *
 * Storage survives across versions of this code and is shared with whatever the
 * browser had before, so every field is checked rather than trusted: a bad record
 * costs the player one game, an unhandled exception costs them the page. The
 * word-shape check on answers and guesses is doing real work — a saved game whose
 * words are the wrong length would render a grid the keyboard cannot fill.
 */
export function load(): Stored {
  const fallback: Stored = {
    boards: DEFAULT_BOARDS,
    length: DEFAULT_LENGTH,
    settings: { ...DEFAULT_SETTINGS },
    game: null,
    stats: {},
  };

  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return fallback; // private browsing, or storage disabled
  }
  if (!raw) return fallback;

  try {
    const parsed = JSON.parse(raw) as Partial<Stored>;
    if (BOARD_COUNTS.includes(parsed.boards as BoardCount)) {
      fallback.boards = parsed.boards as BoardCount;
    }
    if (WORD_LENGTHS.includes(parsed.length as WordLength)) {
      fallback.length = parsed.length as WordLength;
    }
    if (parsed.settings && typeof parsed.settings === 'object') {
      fallback.settings.highContrast = parsed.settings.highContrast === true;
    }
    if (isGame(parsed.game)) fallback.game = parsed.game;
    if (parsed.stats && typeof parsed.stats === 'object') {
      for (const [key, value] of Object.entries(parsed.stats)) {
        if (isStat(value)) fallback.stats[key] = value;
      }
    }
  } catch {
    return fallback;
  }
  return fallback;
}

export function save(state: Stored): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // A full or disabled store is not worth interrupting play over.
  }
}
